import {useCallback, useEffect, useRef, useState} from 'react';

import {
    userContentMigrationPreview,
} from 'src/openapi/admin/apis/UserContentMigrationApi';
import {useAppDispatch} from 'src/store/hooks';

import type {MigrationPreview} from 'src/openapi/admin/models/MigrationPreview';

type UseMigrationPreviewOptions = {
    userId: number,
    templateUserId: number,
    replaceSearches: boolean,
    replaceAlerts: boolean,
    replaceLayouts: boolean,
    autoload?: boolean,
};

export const useMigrationPreview = (
    options: UseMigrationPreviewOptions,
) => {
    const {
        userId,
        templateUserId,
        replaceSearches,
        replaceAlerts,
        replaceLayouts,
        autoload = true,
    } = options;
    const apiDispatch = useAppDispatch();
    const [preview, setPreview] = useState<MigrationPreview | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const requestIdRef = useRef(0);

    const fetchPreview = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        setIsLoading(true);

        try {
            const res = await apiDispatch(
                userContentMigrationPreview({
                    userId,
                    userSearchesReplaceRequest: {
                        templateUserId,
                        replaceSearches,
                        replaceAlerts,
                        replaceLayouts,
                    },
                }),
            );

            if (requestId !== requestIdRef.current) {
                return;
            }

            if (res.payload.success) {
                setPreview(res.payload.data);
            }
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [
        apiDispatch,
        userId,
        templateUserId,
        replaceSearches,
        replaceAlerts,
        replaceLayouts,
    ]);

    useEffect(() => {
        if (autoload) {
            fetchPreview();
        }
    }, [autoload, fetchPreview]);

    return {preview, isLoading};
};
